import React, { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { FiArrowLeft, FiLock } from 'react-icons/fi';
import { RiMailLine, RiUserAddLine, RiCheckboxCircleLine } from 'react-icons/ri';
import { CreditCard } from 'lucide-react';
import { Header } from '../../layout/Header';
import { BottomNav } from '../../layout/BottomNav';
import { useAuth } from '../../../hooks/useAuth';
import { useGetUserProfile, useEditProfileMutation } from '../../../api/profile';

const Spinner = ({ size = 16 }: { size?: number }) => (
  <span
    style={{ width: size, height: size }}
    className="inline-block border-2 border-white/20 border-t-white rounded-full animate-spin"
  />
);

function Toggle({
  checked,
  onChange,
  loading = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <div className="w-11 flex justify-center items-center">
        <Spinner size={16} />
      </div>
    );
  }

  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 ${checked ? 'bg-blue-600' : 'bg-zinc-700'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  );
}

export const SubscriptionsPage: React.FC = () => {
  const { user } = useAuth();
  const { data: profile, isLoading } = useGetUserProfile(user?.id);
  const editProfileMut = useEditProfileMutation();

  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(false);

  useEffect(() => {
    if (profile) {
      setSubscriptionsEnabled(profile.subscriptions_enabled ?? false);
    }
  }, [profile]);

  const handleToggle = async (checked: boolean) => {
    try {
      await editProfileMut.mutateAsync({ subscriptions_enabled: checked });
      setSubscriptionsEnabled(checked);
    } catch (err) {
      console.error('Failed to toggle subscriptions:', err);
    }
  };

  const isEligible = profile?.email_features_eligible ?? profile?.email_features_enabled ?? false;

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-[calc(var(--header-height)+24px)] md:pt-[calc(var(--header-height)+32px)] max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-8 text-zinc-500 text-[12px] font-medium">
          <Link
            to="/profile/settings"
            className="flex items-center gap-1.5 hover:text-white transition-colors"
          >
            <FiArrowLeft size={13} className="text-blue-500/80" />
            <span>Settings</span>
          </Link>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-200">Subscriptions</span>
        </nav>

        {/* Page header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
            <RiMailLine size={20} className="text-blue-400" />
          </div>
          <div>
            <h1 className="text-white font-bold text-[22px] leading-tight">
              Profile Subscriptions
            </h1>
            <p className="text-zinc-500 text-[13px]">
              Let fans subscribe to your profile mailing list
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-60">
            <Spinner size={36} />
          </div>
        ) : !isEligible ? (
          /* ── Ineligible State ── */
          <div className="space-y-4 max-w-2xl">
            <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                  <FiLock size={16} className="text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-white leading-tight">
                    Verification required
                  </p>
                  <p className="text-zinc-500 text-[12px] mt-1 leading-relaxed">
                    Profile subscriptions are locked. To enable a Subscribe button on your profile
                    and start building your mailing list, you need to complete Stripe onboarding, or
                    receive manual approval from Musicade.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5">
              <p className="text-zinc-500 text-[11px] font-semibold uppercase tracking-widest mb-4">
                How to unlock
              </p>
              <div className="space-y-3">
                {[
                  {
                    icon: CreditCard,
                    label: 'Complete Stripe onboarding',
                    sub: 'Set up your payment account to receive payouts',
                    to: '/profile/settings/payments',
                  },
                  // {
                  //   icon: RiCheckboxCircleLine,
                  //   label: 'Complete Identity Verification',
                  //   sub: 'Verify your identity through the Musicade mobile app',
                  //   to: null,
                  // },
                ].map(({ icon: Icon, label, sub, to }) => (
                  <div key={label} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-xl bg-zinc-800 border border-white/5 flex items-center justify-center shrink-0">
                      <Icon size={14} className="text-zinc-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-zinc-200 leading-tight">{label}</p>
                      <p className="text-zinc-600 text-[11px] mt-0.5">{sub}</p>
                    </div>
                    {to && (
                      <Link
                        to={to}
                        className="shrink-0 px-3 py-1.5 rounded-full bg-white text-black text-[11px] font-semibold hover:bg-zinc-200 transition-colors"
                      >
                        Go →
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ── Eligible State ── */
          <div className="space-y-4 max-w-2xl">
            {/* Main toggle card */}
            <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5">
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                  <RiUserAddLine size={16} className="text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-white leading-tight">
                    Enable subscriptions
                  </p>
                  <p className="text-zinc-500 text-[12px] mt-0.5 leading-normal">
                    Show a Subscribe button on your public profile so fans can join your mailing
                    list.
                  </p>
                </div>
                <Toggle
                  checked={subscriptionsEnabled}
                  onChange={handleToggle}
                  loading={editProfileMut.isPending}
                />
              </div>
            </div>

            {/* Info card — shown when toggled on */}
            {subscriptionsEnabled && (
              <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5 space-y-4">
                <p className="text-zinc-500 text-[11px] font-semibold uppercase tracking-widest">
                  What happens next
                </p>
                <div className="space-y-3">
                  {[
                    {
                      icon: RiUserAddLine,
                      color: 'text-blue-400',
                      bg: 'bg-blue-500/10 border-blue-500/20',
                      title: 'Fans can subscribe',
                      sub: 'A Subscribe button will appear on your public profile for all visitors.',
                    },
                    {
                      icon: RiMailLine,
                      color: 'text-green-400',
                      bg: 'bg-green-500/10 border-green-500/20',
                      title: 'Email your subscribers',
                      sub: 'Subscribers are added to your mailing list. Email them anytime from the Mail dashboard.',
                    },
                    {
                      icon: RiCheckboxCircleLine,
                      color: 'text-purple-400',
                      bg: 'bg-purple-500/10 border-purple-500/20',
                      title: 'They can unsubscribe anytime',
                      sub: 'Subscribers can opt out at any time directly from your profile.',
                    },
                  ].map(({ icon: Icon, color, bg, title, sub }) => (
                    <div key={title} className="flex items-start gap-4">
                      <div
                        className={`w-8 h-8 rounded-xl border flex items-center justify-center shrink-0 ${bg}`}
                      >
                        <Icon size={14} className={color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-zinc-200 leading-tight">
                          {title}
                        </p>
                        <p className="text-zinc-600 text-[11px] mt-0.5 leading-normal">{sub}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-3 border-t border-white/5">
                  <Link
                    to="/mail"
                    className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-[12px] font-medium transition-colors"
                  >
                    <RiMailLine size={13} />
                    Go to Mail dashboard
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};
